import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * Composable pour gérer les retours haptiques de l'appareil
 * Fournit des méthodes pour différents types de feedback haptique
 */
export function useHaptics() {
    /**
     * Vérifie si les haptics sont disponibles sur la plateforme actuelle
     */
    const isAvailable = () => {
        return Capacitor.isNativePlatform();
    };

    /**
     * Déclenche un retour haptique d'impact
     * @param style - Style d'impact (Light, Medium, Heavy)
     */
    const impact = async (style: ImpactStyle = ImpactStyle.Medium) => {
        if (!isAvailable()) return;

        try {
            // Le vrai impact natif (UIImpactFeedbackGenerator) donne les
            // intensités Light/Medium/Heavy calibrées par iOS.
            await Haptics.impact({ style });
        } catch (error) {
            console.warn('⚠️ Could not trigger haptic impact:', error);
        }
    };

    /**
     * Déclenche un retour haptique léger
     */
    const impactLight = async () => {
        await impact(ImpactStyle.Light);
    };

    /**
     * Déclenche un retour haptique moyen
     */
    const impactMedium = async () => {
        await impact(ImpactStyle.Medium);
    };

    /**
     * Déclenche un retour haptique fort
     */
    const impactHeavy = async () => {
        await impact(ImpactStyle.Heavy);
    };

    /**
     * Déclenche un retour haptique de notification
     * @param type - Type de notification (Success, Warning, Error)
     */
    const notification = async (type: NotificationType = NotificationType.Success) => {
        if (!isAvailable()) return;

        try {
            await Haptics.notification({ type });
        } catch (error) {
            console.warn('⚠️ Could not trigger haptic notification:', error);
        }
    };

    /**
     * Déclenche un retour haptique de succès
     */
    const notificationSuccess = async () => {
        await notification(NotificationType.Success);
    };

    /**
     * Déclenche un retour haptique d'avertissement
     */
    const notificationWarning = async () => {
        await notification(NotificationType.Warning);
    };

    /**
     * Déclenche un retour haptique d'erreur
     */
    const notificationError = async () => {
        await notification(NotificationType.Error);
    };

    /**
     * Fait vibrer l'appareil
     * @param duration - Durée de la vibration en millisecondes (défaut: 300ms)
     */
    const vibrate = async (duration: number = 300) => {
        if (!isAvailable()) return;

        try {
            await Haptics.vibrate({ duration });
        } catch (error) {
            console.warn('⚠️ Could not vibrate device:', error);
        }
    };

    /**
     * Démarre une sélection haptique
     * Utilisé pour les interactions de sélection (ex: slider, picker)
     */
    const selectionStart = async () => {
        if (!isAvailable()) return;

        try {
            await Haptics.selectionStart();
        } catch (error) {
            console.warn('⚠️ Could not start haptic selection:', error);
        }
    };

    /**
     * Indique qu'une sélection a changé
     * À appeler pendant qu'une sélection est active
     */
    const selectionChanged = async () => {
        if (!isAvailable()) return;

        try {
            await Haptics.selectionChanged();
        } catch (error) {
            console.warn('⚠️ Could not trigger haptic selection change:', error);
        }
    };

    /**
     * Termine une sélection haptique
     * À appeler quand l'utilisateur relève son doigt
     */
    const selectionEnd = async () => {
        if (!isAvailable()) return;

        try {
            await Haptics.selectionEnd();
        } catch (error) {
            console.warn('⚠️ Could not end haptic selection:', error);
        }
    };

    return {
        isAvailable,
        impact,
        impactLight,
        impactMedium,
        impactHeavy,
        notification,
        notificationSuccess,
        notificationWarning,
        notificationError,
        vibrate,
        selectionStart,
        selectionChanged,
        selectionEnd,
    };
}


